import React, { useEffect, useRef, useState } from 'react';
import { useConsent } from '~/app/consent';

export const CookieConsentPanel = () => {
  const { consent, gpcActive, isPanelOpen, closePanel, savePreferences, acceptAll, rejectAll } = useConsent();
  const [analytics, setAnalytics] = useState(consent.analytics);
  const [ads, setAds] = useState(consent.ads);
  const [optout, setOptout] = useState(consent.optout);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (isPanelOpen) {
      setAnalytics(consent.analytics);
      setAds(consent.ads);
      setOptout(consent.optout);
    }
  }, [isPanelOpen, consent]);

  // Keyboard and screen-reader users: move focus into the dialog when it opens,
  // keep Tab inside it, close on Escape, and hand focus back on close.
  useEffect(() => {
    if (!isPanelOpen) return undefined;

    const opener = document.activeElement;
    const dialog = dialogRef.current;
    const getFocusable = () =>
      Array.from(dialog?.querySelectorAll('button, input:not([disabled]), a[href]') || []);

    getFocusable()[0]?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePanel();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, [isPanelOpen, closePanel]);

  if (!isPanelOpen) {
    return null;
  }

  const handleOptoutToggle = (checked) => {
    setOptout(checked);
    if (checked) {
      setAds(false);
    }
  };

  return (
    <div className="cookie-consent__overlay" role="presentation" onClick={closePanel}>
      <div
        ref={dialogRef}
        className="cookie-consent__modal"
        role="dialog"
        aria-modal="true"
        aria-label="Cookie and Tracking Preferences"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="cookie-consent__close" aria-label="Close" onClick={closePanel}>
          &times;
        </button>
        <h4 className="cookie-consent__title">Cookie &amp; Tracking Preferences</h4>
        <p className="cookie-consent__lead">
          Strictly necessary cookies are required for the site to function and cannot be turned off. You can turn
          the other categories on or off and change your choices at any time by returning to this panel.
        </p>

        <div className="cookie-consent__category">
          <div>
            <b>Strictly Necessary</b>
            <p>Required to operate the site, route your call, and maintain security.</p>
          </div>
          <span className="cookie-consent__always">Always active</span>
        </div>

        <div className="cookie-consent__category">
          <div>
            <b>Analytics &amp; Performance</b>
            <p>Helps us understand how visitors use the site so we can improve it.</p>
          </div>
          <label className="cookie-consent__switch">
            <input
              type="checkbox"
              aria-label="Analytics and Performance cookies"
              checked={analytics}
              onChange={(event) => setAnalytics(event.target.checked)}
            />
            <span className="cookie-consent__track" />
          </label>
        </div>

        <div className="cookie-consent__category">
          <div>
            <b>Advertising &amp; Targeting</b>
            <p>Used to measure and improve our marketing.</p>
          </div>
          <label className="cookie-consent__switch">
            <input
              type="checkbox"
              aria-label="Advertising and Targeting cookies"
              checked={ads}
              disabled={optout}
              onChange={(event) => setAds(event.target.checked)}
            />
            <span className="cookie-consent__track" />
          </label>
        </div>

        <div className="cookie-consent__optout">
          <label>
            <input type="checkbox" checked={optout} onChange={(event) => handleOptoutToggle(event.target.checked)} />
            Opt out of the sale or sharing of my personal information
          </label>
          <p>
            Depending on the technologies in use, some of our sharing of personal information may be treated as a
            &ldquo;sale&rdquo; or &ldquo;sharing&rdquo; under California law. Selecting this option opts you out. We
            also honor opt-out preference signals, including Global Privacy Control (GPC).
            {gpcActive && (
              <>
                {' '}
                <em>Applied automatically from your browser&rsquo;s Global Privacy Control signal.</em>
              </>
            )}
            {' '}
            For more detail, see our <a href="/privacy-policy">Privacy Policy</a>.
          </p>
        </div>

        <div className="cookie-consent__actions">
          <button
            type="button"
            className="cookie-consent__btn cookie-consent__btn--save"
            onClick={() => savePreferences({ analytics, ads, optout })}
          >
            Save My Preferences
          </button>
          <button type="button" className="cookie-consent__btn" onClick={rejectAll}>
            Reject All
          </button>
          <button type="button" className="cookie-consent__btn" onClick={acceptAll}>
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};
